import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { ftToM } from '../../utils/unitsUs';

/** Blueprint: docs/SIM_LAB_WALK_CAMERA.md */
export const WALK_EYE_FT = 6;
export const WALK_EYE_M = ftToM(WALK_EYE_FT);

const MOVE_SPEED = 1.6;
const SPRINT_MULT = 2;
const TURN_SPEED = 1.4;
const LOOK_SENS = 0.0022;
const PITCH_MIN = -1.15;
const PITCH_MAX = 1.15;
const LOOK_AHEAD = 2.5;

/** Stair-friendly step limits (meters) — docs/SIM_LAB_WALK_CAMERA.md */
const MAX_STEP_UP = 0.55;
const MAX_STEP_DOWN = 4;
const FLOOR_DAMP = 14;
/** Cast from just above feet so ceilings / upper floors are not first-hit. */
const RAY_ABOVE_SURFACE = MAX_STEP_UP + 0.2;
const RAY_FAR = MAX_STEP_UP + MAX_STEP_DOWN + 0.5;
const MIN_FLOOR_NORMAL_Y = 0.45;

/** Match LabViewer GizmoHelper margin + head size so look-drag ignores the gizmo. */
const GIZMO_ZONE_PX = 120;

const _hitNormal = new THREE.Vector3();
const _faceFwd = new THREE.Vector3();

function isInGizmoZone(clientX, clientY, el) {
  const rect = el.getBoundingClientRect();
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  return x >= rect.width - GIZMO_ZONE_PX && y >= rect.height - GIZMO_ZONE_PX;
}

const MOVE_KEYS = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'KeyQ',
  'KeyE',
  'ShiftLeft',
  'ShiftRight',
]);

const _rayOrigin = new THREE.Vector3();
const _rayDir = new THREE.Vector3(0, -1, 0);
const _raycaster = new THREE.Raycaster();

/**
 * First-person walk at 6 ft eye height above the surface underfoot.
 * Disables orbit while active. Floor Y follows model meshes (stairs).
 */
export default function LabWalkControls({
  enabled = false,
  floorY = 0,
  spawn = null,
  onExitRequest,
  /** Optional ref: { faceAxis(x,y,z) } — view-gizmo reorient while walking */
  lookApiRef = null,
}) {
  const { camera, gl, scene } = useThree();
  const keysRef = useRef(new Set());
  const yawRef = useRef(0);
  const pitchRef = useRef(0);
  const posRef = useRef(new THREE.Vector3());
  const surfaceYRef = useRef(0);
  const draggingRef = useRef(false);
  const lastPtrRef = useRef({ x: 0, y: 0 });
  const seededRef = useRef(false);
  const forward = useRef(new THREE.Vector3());
  const right = useRef(new THREE.Vector3());
  const lookTarget = useRef(new THREE.Vector3());

  // Expose look reorient for the bottom-right view gizmo (docs/SIM_LAB_WALK_CAMERA.md)
  useEffect(() => {
    if (!lookApiRef) return undefined;
    lookApiRef.current = {
      faceAxis: (ax, ay, az) => {
        // Gizmo head at +X means camera sits on +X looking at origin → forward −X
        _faceFwd.set(-ax, -ay, -az);
        if (_faceFwd.lengthSq() < 1e-8) return;
        _faceFwd.normalize();
        yawRef.current = Math.atan2(-_faceFwd.x, -_faceFwd.z);
        pitchRef.current = THREE.MathUtils.clamp(
          Math.asin(THREE.MathUtils.clamp(_faceFwd.y, -1, 1)),
          PITCH_MIN,
          PITCH_MAX
        );
      },
    };
    return () => {
      lookApiRef.current = null;
    };
  }, [lookApiRef]);

  // Seed pose when entering walk mode
  useEffect(() => {
    if (!enabled) {
      seededRef.current = false;
      keysRef.current.clear();
      draggingRef.current = false;
      return undefined;
    }

    const prevFov = camera.fov;
    camera.fov = 60;
    camera.updateProjectionMatrix();

    const baseFloor = Number.isFinite(floorY) ? floorY : 0;
    surfaceYRef.current = baseFloor;
    const eyeY = baseFloor + WALK_EYE_M;
    if (spawn?.position) {
      posRef.current.set(spawn.position[0], eyeY, spawn.position[2]);
      yawRef.current = spawn.yaw ?? 0;
    } else {
      posRef.current.set(0, eyeY, 6);
      yawRef.current = Math.PI;
    }
    pitchRef.current = 0;
    seededRef.current = true;
    applyPose(camera, posRef.current, yawRef.current, pitchRef.current, lookTarget.current);

    return () => {
      camera.fov = prevFov;
      camera.updateProjectionMatrix();
    };
  }, [enabled, floorY, spawn, camera]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA' || e.target?.tagName === 'SELECT') {
        return;
      }
      if (e.code === 'Escape') {
        e.preventDefault();
        onExitRequest?.();
        return;
      }
      if (MOVE_KEYS.has(e.code)) {
        e.preventDefault();
        keysRef.current.add(e.code);
      }
    };
    const onKeyUp = (e) => {
      keysRef.current.delete(e.code);
    };

    const el = gl.domElement;
    const onPtrDown = (e) => {
      if (e.button !== 0 && e.button !== 2) return;
      // Let the view gizmo receive clicks (orbit tween is ignored; lookApi handles it)
      if (isInGizmoZone(e.clientX, e.clientY, el)) return;
      draggingRef.current = true;
      lastPtrRef.current = { x: e.clientX, y: e.clientY };
    };
    const onPtrUp = () => {
      draggingRef.current = false;
    };
    const onPtrMove = (e) => {
      if (!draggingRef.current) return;
      const dx = e.clientX - lastPtrRef.current.x;
      const dy = e.clientY - lastPtrRef.current.y;
      lastPtrRef.current = { x: e.clientX, y: e.clientY };
      yawRef.current -= dx * LOOK_SENS;
      pitchRef.current = THREE.MathUtils.clamp(
        pitchRef.current - dy * LOOK_SENS,
        PITCH_MIN,
        PITCH_MAX
      );
    };
    const onContextMenu = (e) => e.preventDefault();

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    el.addEventListener('pointerdown', onPtrDown);
    window.addEventListener('pointerup', onPtrUp);
    window.addEventListener('pointermove', onPtrMove);
    el.addEventListener('contextmenu', onContextMenu);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      el.removeEventListener('pointerdown', onPtrDown);
      window.removeEventListener('pointerup', onPtrUp);
      window.removeEventListener('pointermove', onPtrMove);
      el.removeEventListener('contextmenu', onContextMenu);
    };
  }, [enabled, gl, onExitRequest]);

  useFrame((_, dt) => {
    if (!enabled || !seededRef.current) return;
    const keys = keysRef.current;
    const sprint = keys.has('ShiftLeft') || keys.has('ShiftRight');
    const speed = MOVE_SPEED * (sprint ? SPRINT_MULT : 1) * Math.min(dt, 0.05);

    if (keys.has('KeyQ')) yawRef.current += TURN_SPEED * dt;
    if (keys.has('KeyE')) yawRef.current -= TURN_SPEED * dt;

    forward.current.set(-Math.sin(yawRef.current), 0, -Math.cos(yawRef.current));
    right.current.set(Math.cos(yawRef.current), 0, -Math.sin(yawRef.current));

    if (keys.has('KeyW') || keys.has('ArrowUp')) {
      posRef.current.addScaledVector(forward.current, speed);
    }
    if (keys.has('KeyS') || keys.has('ArrowDown')) {
      posRef.current.addScaledVector(forward.current, -speed);
    }
    if (keys.has('KeyA') || keys.has('ArrowLeft')) {
      posRef.current.addScaledVector(right.current, -speed);
    }
    if (keys.has('KeyD') || keys.has('ArrowRight')) {
      posRef.current.addScaledVector(right.current, speed);
    }

    const sampled = sampleWalkSurfaceY(
      scene,
      posRef.current.x,
      posRef.current.z,
      surfaceYRef.current
    );
    if (sampled != null) {
      const prev = surfaceYRef.current;
      const up = sampled.y - prev;
      if (up > MAX_STEP_UP) {
        // Block climb onto tall surfaces (walls / counters); keep prior surface.
      } else if (up < -MAX_STEP_DOWN) {
        // Ignore absurd drops (missed mesh → pad).
      } else if (sampled.isPad && up < -MAX_STEP_UP) {
        // Pad/apron only counts as floor near its own level. If the model
        // floor mesh vanishes (layer/feature toggled off), hold the current
        // surface instead of falling to the pad — the floor usually comes
        // back, and re-adding it must not leave the camera underneath it.
      } else {
        surfaceYRef.current = sampled.y;
      }
    }

    const targetEyeY = surfaceYRef.current + WALK_EYE_M;
    posRef.current.y = THREE.MathUtils.damp(posRef.current.y, targetEyeY, FLOOR_DAMP, dt);
    applyPose(camera, posRef.current, yawRef.current, pitchRef.current, lookTarget.current);
  });

  return null;
}

function applyPose(camera, pos, yaw, pitch, lookTarget) {
  camera.position.copy(pos);
  const lookDist = LOOK_AHEAD;
  lookTarget.set(
    pos.x - Math.sin(yaw) * lookDist * Math.cos(pitch),
    pos.y + Math.sin(pitch) * lookDist,
    pos.z - Math.cos(yaw) * lookDist * Math.cos(pitch)
  );
  camera.lookAt(lookTarget);
  camera.updateMatrixWorld();
}

/**
 * Downward ray from just above the current surface → walkable Y.
 * Prefer near-horizontal model meshes; fall back to lab pad.
 * @returns {{ y: number, isPad: boolean } | null}
 */
function sampleWalkSurfaceY(scene, x, z, surfaceY) {
  _rayOrigin.set(x, surfaceY + RAY_ABOVE_SURFACE, z);
  _raycaster.set(_rayOrigin, _rayDir);
  _raycaster.far = RAY_FAR;
  _raycaster.near = 0;

  const hits = _raycaster.intersectObject(scene, true);
  if (!hits.length) return null;

  let surfaceHit = null;
  let padHit = null;
  for (let i = 0; i < hits.length; i += 1) {
    const h = hits[i];
    const ud = h.object?.userData || {};
    if (ud.labWalkIgnore) {
      if (padHit == null) padHit = h;
      continue;
    }
    if (!h.object?.isMesh || !ud.labWalkSurface) continue;
    if (!isWalkableHit(h)) continue;
    surfaceHit = h;
    break;
  }

  if (surfaceHit) return { y: surfaceHit.point.y, isPad: false };
  if (padHit) return { y: padHit.point.y, isPad: true };
  return null;
}

function isWalkableHit(hit) {
  if (hit.face) {
    _hitNormal.copy(hit.face.normal).transformDirection(hit.object.matrixWorld);
    if (_hitNormal.y < MIN_FLOOR_NORMAL_Y) return false;
  }
  return true;
}

/**
 * Build spawn from localize entry / footprint.
 */
export function buildWalkSpawn(localize) {
  if (!localize) {
    return { position: [0, WALK_EYE_M, 6], yaw: Math.PI };
  }
  const floorY = Number.isFinite(localize.floorY) ? localize.floorY : 0;
  const entry = localize.entry;
  const center = localize.center || [0, 0, 0];
  const outward = entry?.outward || [0, 0, 1];
  const point = entry?.point || [center[0], floorY + WALK_EYE_M, center[2]];

  const position = [
    point[0] + outward[0] * 1.5,
    floorY + WALK_EYE_M,
    point[2] + outward[2] * 1.5,
  ];

  // Face into the building (−outward)
  const yaw = Math.atan2(outward[0], outward[2]);

  return { position, yaw, floorY };
}
